import type { DropdownOption } from "../../components/molecules/Dropdown";
import type { DateGrain } from "../../components/molecules/StoryFilterBar/StoryFilterBar.types";
import type { FieldValuesResponseApi, FilterScalarApi } from "./storyFilters.types";

/** The data range from the values lookup, split by the editor that reads it. */
export interface FieldValueRanges {
  /** A continuous field's range, for the number editor. */
  numberRange: { min: number; max: number } | null;
  /** A temporal field's range as `YYYY-MM-DD` dates, for the date editor. */
  dateRange: { min: string; max: string } | null;
}

/**
 * The lookup's `min`/`max` sorted by type: numbers make a number range, date
 * strings a date range. Both null for a categorical field, or when the two
 * ends don't come back as the same type, so an editor never gets half a range.
 */
export const toFieldValueRanges = (
  page: Pick<FieldValuesResponseApi, "min" | "max"> | undefined,
): FieldValueRanges => {
  const min = page?.min ?? null;
  const max = page?.max ?? null;
  return {
    numberRange: typeof min === "number" && typeof max === "number" ? { min, max } : null,
    dateRange: typeof min === "string" && typeof max === "string" ? { min, max } : null,
  };
};

/** ISO yyyy-mm-dd strings compare correctly as plain strings. */
export interface DateRangeBoundsInput {
  from: string;
  to: string;
  /** Earliest date the field has data for (from the values lookup). */
  min?: string;
  /** Latest date the field has data for. */
  max?: string;
}

export interface DateRangeBounds {
  fromMin?: string;
  fromMax?: string;
  toMin?: string;
  toMax?: string;
}

const laterOf = (a?: string, b?: string) => (a && b ? (a > b ? a : b) : a ?? b);
const earlierOf = (a?: string, b?: string) => (a && b ? (a < b ? a : b) : a ?? b);

/**
 * "From" may not pass "To" and "To" may not precede "From", and both stay inside the
 * field's data range, so the user can never build an inverted or empty range.
 */
export const toDateRangeBounds = ({ from, to, min, max }: DateRangeBoundsInput): DateRangeBounds => ({
  fromMin: min,
  fromMax: earlierOf(to || undefined, max),
  toMin: laterOf(from || undefined, min),
  toMax: max,
});

/** True when both ends are set and ordered; the Apply button stays off otherwise. */
export const isDateRangeComplete = (from: string, to: string): boolean =>
  Boolean(from) && Boolean(to) && from <= to;

// ─── Year / quarter / month fields: pick periods from a list ───────────────────

/** Grains picked from a list of periods; a day is picked on the calendar instead. */
export type PeriodGrain = Exclude<DateGrain, "day">;

const MONTHS_PER_PERIOD: Record<PeriodGrain, number> = { year: 12, quarter: 3, month: 1 };

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** A `YYYY-MM-DD` string as a UTC midnight date; null when it isn't a real day. */
const parseIsoDate = (iso: string): Date | null => {
  const match = ISO_DATE.exec(iso);
  if (!match) return null;
  const date = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(date.getTime()) || date.toISOString().slice(0, 10) !== iso ? null : date;
};

const toIsoDate = (date: Date): string => date.toISOString().slice(0, 10);

/** The first day of the period `iso` falls in: `2016-11-17` is `2016-10-01` for a quarter. Null for a bad date. */
export const toGrainStart = (iso: string, grain: PeriodGrain): string | null => {
  const date = parseIsoDate(iso);
  if (!date) return null;
  const month = date.getUTCMonth();
  const startMonth = month - (month % MONTHS_PER_PERIOD[grain]);
  return toIsoDate(new Date(Date.UTC(date.getUTCFullYear(), startMonth, 1)));
};

const LABEL_FORMAT: Record<Exclude<DateGrain, "quarter">, Intl.DateTimeFormatOptions> = {
  year: { year: "numeric" },
  month: { month: "short", year: "numeric" },
  day: { month: "short", day: "numeric", year: "numeric" },
};

/** A period start as people read it at its grain: `2016`, `Q3 2016`, `Nov 2016`, `Nov 17, 2016`. A bad date is shown as sent. */
export const toPeriodLabel = (iso: string, grain: DateGrain): string => {
  const date = parseIsoDate(iso);
  if (!date) return iso;
  if (grain === "quarter") return `Q${Math.floor(date.getUTCMonth() / 3) + 1} ${date.getUTCFullYear()}`;
  return new Intl.DateTimeFormat("en-US", { ...LABEL_FORMAT[grain], timeZone: "UTC" }).format(date);
};

/**
 * Every period from the field's first to its last, oldest first, as dropdown
 * options valued by their period start. Ends that aren't period starts are
 * floored to one. Empty without both ends, or when they are out of order.
 */
export const toPeriodOptions = (grain: PeriodGrain, min?: string, max?: string): DropdownOption<string>[] => {
  const first = min ? toGrainStart(min, grain) : null;
  const last = max ? toGrainStart(max, grain) : null;
  if (!first || !last || first > last) return [];
  const options: DropdownOption<string>[] = [];
  const cursor = new Date(`${first}T00:00:00Z`);
  while (toIsoDate(cursor) <= last) {
    const value = toIsoDate(cursor);
    options.push({ value, label: toPeriodLabel(value, grain) });
    cursor.setUTCMonth(cursor.getUTCMonth() + MONTHS_PER_PERIOD[grain]);
  }
  return options;
};

export interface PeriodRangeOptionsInput {
  grain: PeriodGrain;
  /** The field's first and last period, from the values lookup. */
  min?: string;
  max?: string;
  /** The picked period starts; empty when not chosen. */
  from: string;
  to: string;
}

/**
 * The From and To lists for a period range. Periods after the picked To are
 * greyed in From, and periods before the picked From are greyed in To, so the
 * range can't invert (the same rule `toDateRangeBounds` applies to days).
 */
export const toPeriodRangeOptions = ({
  grain,
  min,
  max,
  from,
  to,
}: PeriodRangeOptionsInput): { fromOptions: DropdownOption<string>[]; toOptions: DropdownOption<string>[] } => {
  const periods = toPeriodOptions(grain, min, max);
  return {
    fromOptions: periods.map((option) => (to && option.value > to ? { ...option, disabled: true } : option)),
    toOptions: periods.map((option) => (from && option.value < from ? { ...option, disabled: true } : option)),
  };
};

/** True when `a` sorts after `b`: numbers by size, ISO dates and text by their characters; never across types. */
export const isScalarAfter = (a: FilterScalarApi, b: FilterScalarApi): boolean =>
  (typeof a === "number" && typeof b === "number") || (typeof a === "string" && typeof b === "string") ? a > b : false;

/** Draft text from a number input → a finite number, or null for empty or unparseable text. */
export const parseRangeNumber = (text: string): number | null => {
  if (text.trim() === "") return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Placeholder hint for a number end, e.g. `Min 1.5`; empty when the lookup gave no bound. */
export const toRangePlaceholder = (label: "Min" | "Max", bound: number | null | undefined): string =>
  bound === null || bound === undefined ? label : `${label} ${bound}`;

export interface NumberRangeInput {
  from: string;
  to: string;
  min?: number | null;
  max?: number | null;
}

/**
 * A number range is valid when both ends parse, From ≤ To, and both sit inside the
 * field's data range. Anything else keeps Apply off and shows the fields red.
 */
export const isNumberRangeComplete = ({ from, to, min, max }: NumberRangeInput): boolean => {
  const low = parseRangeNumber(from);
  const high = parseRangeNumber(to);
  if (low === null || high === null || low > high) return false;
  if (min !== null && min !== undefined && low < min) return false;
  if (max !== null && max !== undefined && high > max) return false;
  return true;
};

/** Something is typed but the range can't be applied, so the fields should show an error. */
export const isNumberRangeInvalid = (input: NumberRangeInput): boolean =>
  Boolean(input.from.trim() && input.to.trim()) && !isNumberRangeComplete(input);
